import * as Sentry from "@sentry/react";

/**
 * Acceso a `localStorage` / `sessionStorage` que NUNCA lanza.
 *
 * Arregla MB-APP-EXPRESS-151 y MB-APP-EXPRESS-182
 * (`SecurityError: Failed to read the 'localStorage' property from 'Window'`,
 * DOMException 18). En Edge/Windows con el almacenamiento del sitio bloqueado
 * el que lanza es el ACCESO A LA PROPIEDAD `window.localStorage`, antes de
 * llamar a ningun metodo. `window.sessionStorage` tiene exactamente el mismo
 * modo de fallo —misma Edge, misma excepcion, mismo getter— asi que los dos
 * salen de la MISMA fabrica y no de dos envoltorios parecidos. Por eso:
 *
 * - Ninguna comprobacion de existencia sirve: `typeof localStorage`,
 *   `if (localStorage)` y `localStorage != null` pasan por el mismo getter y
 *   lanzan igual. Lo unico que funciona es try/catch alrededor de la lectura.
 * - El try/catch envuelve TAMBIEN la llamada al metodo: `setItem` puede lanzar
 *   `QuotaExceededError` aunque el almacenamiento sea legible.
 * - No se memoiza la disponibilidad. Una sonda al cargar el modulo latchea el
 *   estado equivocado (en Vitest+happy-dom `window.localStorage` es
 *   `undefined`, y los stubs de globales de los tests entran y salen), asi que
 *   un flag cacheado vuelve el comportamiento dependiente del orden de
 *   ejecucion. El costo real son ~8 lecturas por sesion.
 *
 * Cuando el almacenamiento real no esta disponible se usa un Map en memoria
 * del modulo (uno por area: `local` y `session` no se mezclan). NO es
 * cosmetico: un guard a secas convierte el crash en datos silenciosamente
 * incorrectos (un reporte de buro archivado como solicitante principal en vez
 * de obligado solidario, o una cuenta creada por invitacion que nunca se une a
 * la organizacion). El fallback mantiene esos dos flujos correctos durante
 * toda la sesion del SPA. Limite conocido: no sobrevive una recarga completa
 * ni una pestana nueva.
 *
 * Ese limite es irrelevante para datos de pantalla, pero es DECISIVO para
 * quien necesita una marca que sobreviva a la recarga (la guarda anti-bucle de
 * `utils/lazyWithReload`): un contador que se olvida en cada arranque concede
 * recargas para siempre. De ahi `isAvailable()` y `getPersistedItem()`, que
 * dejan ver la diferencia entre "guardado de verdad" y "guardado en memoria"
 * sin obligar a nadie a tocar el global crudo.
 *
 * Sentry: SOLO migajas, JAMAS `captureException`/`captureMessage`. Una captura
 * aqui abriria un issue nuevo en cada pageview afectado, que es exactamente lo
 * que este arreglo debe evitar. Y la migaja no lleva ni el nombre de la llave
 * ni el valor: `beforeSend` en `src/sentry.ts` reemplaza por "[Filtered]"
 * cualquier migaja cuyo mensaje o `data` contenga "token", "auth", "credit",
 * etc., asi que una migaja que nombrara `invite_token` llegaria vacia.
 */

type StorageMethod = "getItem" | "setItem" | "removeItem";
type StorageOp = "get" | "set" | "remove";

/** Etiqueta del area, tal como viaja en la migaja (`data.api`). */
type StorageArea = "local" | "session";

export interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /**
   * `true` solo si el almacenamiento REAL del navegador es legible y tiene los
   * tres metodos AHORA MISMO. No es una sonda cacheada: se vuelve a leer el
   * getter en cada llamada, por lo mismo que el resto del helper.
   *
   * Para que sirve: distinguir "estoy guardando de verdad" de "estoy guardando
   * en el Map en memoria". Quien solo necesita que el dato sobreviva a la
   * pantalla puede ignorarla; quien necesita que sobreviva a una RECARGA (la
   * guarda anti-bucle de `lazyWithReload`) tiene que consultarla, porque el
   * fallback en memoria se borra justo cuando se cambia de documento.
   */
  isAvailable(): boolean;
  /**
   * Lee SOLO el almacenamiento real: `null` si no esta disponible, si el
   * metodo lanza o si la llave no esta. NUNCA consulta el fallback en memoria.
   *
   * Es la lectura que necesita un read-back honesto: hay navegadores (y modos
   * privados) donde `setItem` no lanza pero tampoco persiste, y `getItem` de
   * aqui arriba taparia ese fallo devolviendo el valor del Map.
   */
  getPersistedItem(key: string): string | null;
}

interface SafeStorageHandle {
  storage: SafeStorage;
  /** Solo tests: ver `__resetSafeStorageForTests`. */
  reset: () => void;
}

/**
 * Fabrica el envoltorio de un area. Un solo cuerpo para las dos areas: el
 * unico que cambia es QUE getter se lee y que etiqueta lleva la migaja.
 */
function createSafeStorage(area: StorageArea): SafeStorageHandle {
  const memory = new Map<string, string>();

  /** Una migaja por tipo de operacion y por sesion: el anillo de migajas de
   * Sentry guarda 100 y es la memoria de "que paso justo antes" del siguiente
   * error de la pagina. No conviene inundarlo. */
  const breadcrumbEmitted = new Set<StorageOp>();

  /**
   * Devuelve el `Storage` real solo si se puede leer la propiedad y el metodo
   * que se va a usar existe; `null` en cualquier otro caso. Valida un metodo a
   * la vez a proposito: asi un doble parcial (o un navegador viejo con un
   * `Storage` incompleto) sigue sirviendo para las operaciones que si soporta.
   */
  const realStore = (method: StorageMethod): Storage | null => {
    try {
      // El unico acceso crudo del repo, y por eso este archivo es la unica
      // entrada real del allowlist de `tests/utils/noRawStorage.test.ts`:
      // LEER esta propiedad es lo que lanza, de ahi el try/catch de fuera.
      const store =
        area === "local" ? window.localStorage : window.sessionStorage;

      if (!store || typeof store[method] !== "function") {
        return null;
      }

      return store;
    } catch {
      return null;
    }
  };

  const noteUnavailable = (op: StorageOp): void => {
    if (breadcrumbEmitted.has(op)) {
      return;
    }
    breadcrumbEmitted.add(op);

    try {
      Sentry.addBreadcrumb({
        category: "storage",
        message: "storage.unavailable",
        level: "info",
        // Sin nombre de llave y sin valor: ver beforeSend en src/sentry.ts.
        data: { api: area, op },
      });
    } catch {
      // Sentry no puede romper una operacion de almacenamiento.
    }
  };

  const readFallback = (key: string): string | null => {
    const value = memory.get(key);
    return value === undefined ? null : value;
  };

  const storage: SafeStorage = {
    getItem(key: string): string | null {
      const store = realStore("getItem");

      if (store) {
        try {
          const value = store.getItem(key);
          // `null` puede significar "no esta" o "no se pudo persistir antes"
          // (QuotaExceededError), de ahi la consulta al fallback.
          return value === null ? readFallback(key) : value;
        } catch {
          noteUnavailable("get");
          return readFallback(key);
        }
      }

      noteUnavailable("get");
      return readFallback(key);
    },

    setItem(key: string, value: string): void {
      const store = realStore("setItem");

      if (store) {
        try {
          store.setItem(key, value);
          // Escritura real correcta: el fallback no debe sombrearla nunca.
          memory.delete(key);
          return;
        } catch {
          // QuotaExceededError y familia: cae a memoria.
        }
      }

      noteUnavailable("set");
      memory.set(key, value);
    },

    removeItem(key: string): void {
      const store = realStore("removeItem");

      if (store) {
        try {
          store.removeItem(key);
        } catch {
          noteUnavailable("remove");
        }
      } else {
        noteUnavailable("remove");
      }

      memory.delete(key);
    },

    isAvailable(): boolean {
      // Los tres metodos: un doble parcial no sirve para una marca durable.
      // Sin migaja: esto es una consulta, no una operacion fallida.
      return (
        realStore("getItem") !== null &&
        realStore("setItem") !== null &&
        realStore("removeItem") !== null
      );
    },

    getPersistedItem(key: string): string | null {
      const store = realStore("getItem");
      if (!store) {
        noteUnavailable("get");
        return null;
      }
      try {
        return store.getItem(key);
      } catch {
        noteUnavailable("get");
        return null;
      }
    },
  };

  return {
    storage,
    reset: () => {
      memory.clear();
      breadcrumbEmitted.clear();
    },
  };
}

const local = createSafeStorage("local");
const session = createSafeStorage("session");

export const safeLocalStorage = local.storage;

/**
 * Misma semantica que `safeLocalStorage`, sobre `sessionStorage`: el dato
 * muere al cerrar la pestana.
 *
 * Quien lo usa hoy: la guarda anti-bucle de `utils/lazyWithReload`, cuyo
 * presupuesto de recargas DEBE morir con la pestana (si viviera en
 * `localStorage`, una recarga legitima de la semana que viene se quedaria sin
 * presupuesto). Ese consumidor ademas comprueba `isAvailable()` antes de
 * conceder nada: ver el comentario de esa funcion.
 */
export const safeSessionStorage = session.storage;

/**
 * Solo para tests: limpia el fallback en memoria y el latch de migajas de las
 * DOS areas para que un test no herede el estado del anterior. Nada de
 * produccion lo usa.
 */
export const __resetSafeStorageForTests = (): void => {
  local.reset();
  session.reset();
};

/** Alias historico de `__resetSafeStorageForTests`. Solo tests. */
export const __resetSafeLocalStorageForTests = __resetSafeStorageForTests;
